import React from "react";
import { Phone, Mail, MapPin, ArrowUp } from "lucide-react";
import { useSettings } from "@/hooks/useSettings";
import { useNavigate } from "react-router-dom";

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navLinks = [
    { label: "Accueil", id: "hero" },
    { label: "Solutions", id: "solution" },
    { label: "Packs", id: "use-cases" },
    { label: "Calculateur ROI", id: "calculator" },
    { label: "FAQ", id: "faq" },
  ];

  const logoDark = settings?.logo_dark;

  return (
    <footer data-testid="footer" className="bg-slate-900 border-t-4 border-accent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
          {/* Brand Column */}
          <div className="md:col-span-1">
            {logoDark ? (
              <img 
                src={logoDark} 
                alt="Lead Artisan" 
                className="h-12 w-auto mb-6"
              />
            ) : (
              <button
                onClick={scrollToTop}
                className="text-2xl font-black text-white mb-6 block" 
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                <span className="text-accent">Lead</span>Artisan
              </button>
            )}
            <p className="text-slate-400 leading-relaxed mb-6 text-sm">
              Infrastructure IA complète pour les artisans en Suisse. Appels, relances, CRM, publicité, rendez-vous — tout est automatisé.
            </p>
          </div>

          {/* Navigation Column */}
          <div>
            <h3 className="section-label text-accent mb-6">Navigation</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    data-testid={`footer-link-${link.id}`}
                    className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="section-label text-accent mb-6">Contact</h3>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <Phone className="w-4 h-4 text-accent flex-shrink-0" />
                <span>Démo gratuite disponible</span>
              </li>
              <li className="flex items-center gap-3 text-slate-400 text-sm">
                <MapPin className="w-4 h-4 text-accent flex-shrink-0" />
                <span>Suisse Romande</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-slate-500">
            &copy; {currentYear} Lead Artisan. Tous droits réservés.
          </p>
          <div className="flex items-center gap-6">
            <button 
              onClick={() => navigate("/cgv")}
              data-testid="footer-cgv-link"
              className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
            >
              Conditions générales
            </button>
            <button 
              onClick={() => navigate("/diagnostic")}
              data-testid="footer-diagnostic-link"
              className="text-sm text-slate-500 hover:text-slate-300 transition-colors"
            >
              Diagnostic gratuit
            </button>
            <button className="text-sm text-slate-500 hover:text-slate-300 transition-colors">
              Confidentialité
            </button>
            <button
              onClick={scrollToTop}
              data-testid="footer-scroll-top"
              className="w-10 h-10 rounded-md bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-400 hover:text-accent hover:border-accent transition-colors"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
